import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  SettingsNav,
  SETTINGS_SECTIONS,
  type SettingsSection,
} from './SettingsNav';
import { PersonalSection } from './sections/PersonalSection';
import { PaymentSection } from './sections/PaymentSection';
import { NotificationsSection } from './sections/NotificationsSection';
import { LearningStyleSection } from './sections/LearningStyleSection';
import { AppearanceSection } from './sections/AppearanceSection';
import { ReportProblemSection } from './sections/ReportProblemSection';
import { SettingsHome, SettingsSectionScreen } from './SettingsPhone';
import { useAuth } from '@/contexts/AuthContext';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { notify } from '@/lib/notify';

/**
 * AccountSection (now folded into Personal) and the standalone
 * LessonFlowSection (now embedded inside Learning Style) are
 * intentionally NOT referenced here so they stay out of the active nav,
 * but their source files are kept so we can re-enable them later
 * without a re-import scavenger hunt.
 */
const SECTION_COMPONENTS: Record<
  SettingsSection,
  { component: React.FC; title: string; subtitle: string }
> = {
  personal: {
    component: PersonalSection,
    title: 'Account',
    subtitle: 'Manage your profile, password, and account.',
  },
  payment: {
    component: PaymentSection,
    title: 'Payment & billing',
    subtitle: 'Manage your subscription, payment method, and billing history.',
  },
  notifications: {
    component: NotificationsSection,
    title: 'Notifications',
    subtitle: 'Choose what notifications you receive and how.',
  },
  'learning-style': {
    component: LearningStyleSection,
    title: 'Learning style',
    subtitle: 'Customize your learning experience to match your preferences.',
  },
  appearance: {
    component: AppearanceSection,
    title: 'Appearance',
    subtitle: 'Personalize how Xonera looks with light, dark, or system theme.',
  },
  report: {
    component: ReportProblemSection,
    title: 'Report a problem',
    subtitle:
      'Found a bug or incorrect content? Tell us what happened. Every report helps.',
  },
};

/** `?tab=` narrowed to a real section, or null. */
function sectionFrom(tab: string | null): SettingsSection | null {
  return tab && tab in SECTION_COMPONENTS ? (tab as SettingsSection) : null;
}

export function SettingsLayout() {
  // `?tab=payment` is how every billing prompt links here — the budget
  // paywall card, the budget-exceeded dialog and the billing banner. The
  // layout used to ignore it and always open on Account, so "Upgrade" landed
  // the user on their profile. Seeded from the URL, and followed if it
  // changes while Settings is already open (the banner, clicked from here).
  //
  // Keyed on the location's `key` as well as the param: picking a section is
  // local state and leaves the URL alone, so a user who arrived on
  // `?tab=payment` and moved to Account is still AT `?tab=payment`. Following
  // the banner's link again then produces the same param, and an effect keyed
  // on the param alone never re-ran — the click did nothing. Every navigation
  // gets a fresh `key`, so the link is honoured each time it is followed.
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { key: locationKey } = location;
  const navigate = useNavigate();
  // Below `md`: the phone's list-then-section screens (`SettingsPhone`).
  const phone = useMediaQuery('(max-width: 767.98px)');
  const requested = sectionFrom(searchParams.get('tab'));
  const [activeSection, setActiveSection] = useState<SettingsSection>(
    requested ?? 'personal'
  );
  useEffect(() => {
    if (requested) setActiveSection(requested);
  }, [requested, locationKey]);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      // `signOut` reports failure in its result rather than throwing, so a
      // failed sign-out used to hand the button back with no word of why —
      // the user was still signed in and nothing on screen said so.
      const { error } = await signOut();
      if (error) {
        notify.error(
          "Couldn't sign you out. Check your connection and try again."
        );
      }
    } finally {
      setIsSigningOut(false);
    }
  };

  // On a phone the screen IS the URL, read directly: `activeSection` follows
  // the URL one render late (an effect), which mounted the PREVIOUS section
  // for a frame on every open - a flash of the wrong screen, and its fetch.
  const {
    component: ActiveComponent,
    title,
    subtitle,
  } = SECTION_COMPONENTS[(phone && requested) || activeSection];

  if (phone) {
    // On a phone the URL is the screen: no `?tab=` is the list, a section is
    // pushed as its own history entry - so the phone's back gesture returns
    // to the list, and a link to `?tab=payment` (every billing prompt) opens
    // straight on it.
    if (!requested) {
      return (
        <SettingsHome
          onOpen={section => {
            // Only `tab` changes; anything else on the URL stays.
            const next = new URLSearchParams(searchParams);
            next.set('tab', section);
            navigate(
              { search: `?${next}` },
              { state: { fromSettingsHome: true } }
            );
          }}
          onSignOut={handleSignOut}
          isSigningOut={isSigningOut}
        />
      );
    }
    const fromHome = Boolean(
      (location.state as { fromSettingsHome?: boolean } | null)
        ?.fromSettingsHome
    );
    return (
      <SettingsSectionScreen
        title={title}
        subtitle={subtitle}
        // Back to the list: a step back in history when the list is where
        // the user came from, or a replace when they arrived on a link.
        onBack={() =>
          fromHome ? navigate(-1) : navigate('/settings', { replace: true })
        }
      >
        <ActiveComponent />
      </SettingsSectionScreen>
    );
  }

  return (
    <div className='flex h-full flex-col gap-4'>
      {/* Two-panel layout - full height */}
      <div className='flex min-h-0 flex-1 gap-6'>
        {/* Left nav panel */}
        <div className='w-64 flex-shrink-0 overflow-y-auto rounded-xl border border-border/60 bg-card/70 backdrop-blur-sm dark:bg-card'>
          <SettingsNav
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            onSignOut={handleSignOut}
            isSigningOut={isSigningOut}
          />
        </div>

        {/* Right content panel */}
        <div className='flex min-w-0 flex-1 flex-col rounded-xl border border-border/60 bg-card/70 backdrop-blur-sm dark:bg-card'>
          {/* Sticky header - animated alongside the content so the title
              re-introduces itself when the user switches tabs. */}
          <div
            key={`${activeSection}-header`}
            className='flex-shrink-0 border-b border-border/60 px-6 pb-4 pt-6 duration-200 animate-in fade-in-50 slide-in-from-bottom-1 sm:px-8 sm:pt-8'
          >
            <h2 className='text-lg font-semibold text-foreground'>{title}</h2>
            <p className='mt-1 text-sm text-muted-foreground'>{subtitle}</p>
          </div>

          {/* Scrollable content - keyed on the active section so swapping
              tabs replays the fade + slide animation instead of just
              swapping the DOM. */}
          <div
            key={`${activeSection}-body`}
            className='min-h-0 flex-1 overflow-y-auto px-6 py-6 duration-300 animate-in fade-in-50 slide-in-from-bottom-2 sm:px-8'
          >
            <ActiveComponent />
          </div>
        </div>
      </div>
    </div>
  );
}
